import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import useSeo from "@/hooks/useSeo";
import ComicCard from "@/components/common/ComicCard";
import Breadcrumb from "@/components/common/Breadcrumb";
import { categories, comics } from "@/mocks/comics";

const filters = [{ slug: "all", name: "全部", icon: "ri-stack-line" }, ...categories];

export default function Rankings() {
  useSeo({
    title: "漫画推荐榜单 - 必看漫画/高分漫画/冷门佳作/补番清单 | 我爱漫画网",
    description:
      "我爱漫画网推荐榜单，聚合必看漫画、高分漫画、冷门佳作与补番清单，口碑漫画推荐一网打尽，帮你快速找到好漫画。",
    path: "/rankings",
  });

  const [searchParams, setSearchParams] = useSearchParams();
  const q = searchParams.get("q") ?? "";
  const [active, setActive] = useState("all");

  const list = useMemo(() => {
    const keyword = q.trim().toLowerCase();
    return comics.filter((c) => {
      const matchCat = active === "all" || c.category === active;
      if (!matchCat) return false;
      if (!keyword) return true;
      return (
        c.title.toLowerCase().includes(keyword) ||
        c.author.toLowerCase().includes(keyword) ||
        c.genres.some((g) => g.toLowerCase().includes(keyword)) ||
        c.tags.some((t) => t.toLowerCase().includes(keyword))
      );
    });
  }, [active, q]);

  const setKeyword = (value: string) => {
    if (value) setSearchParams({ q: value });
    else setSearchParams({});
  };

  return (
    <>
      <section className="bg-background-100">
        <div className="container-page flex flex-col items-center py-12 text-center md:py-16">
          <span className="mb-3 inline-flex items-center gap-2 rounded-full bg-accent-100 px-4 py-1.5 text-sm font-medium text-accent-700">
            <i className="ri-trophy-line" />
            我爱漫画网 · 推荐榜单
          </span>
          <h1 className="font-heading text-2xl font-bold text-foreground-950 md:text-4xl">
            全站漫画推荐榜单
          </h1>
          <p className="mt-3 max-w-xl text-sm leading-relaxed text-foreground-600 md:text-base">
            必看漫画、高分漫画、冷门佳作与补番清单全收录，可按栏目筛选或直接搜索作品与类型。
          </p>
          <div className="mt-6 flex w-full max-w-lg items-center gap-2 rounded-full border border-background-200 bg-background-50 px-4 py-3">
            <i className="ri-search-line text-base text-foreground-500" />
            <input
              type="search"
              defaultValue={q}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="搜索作品、作者、类型或标签..."
              className="w-full bg-transparent text-sm outline-none placeholder:text-foreground-400"
              aria-label="搜索漫画"
            />
            {q && (
              <button type="button" onClick={() => setKeyword("")} className="text-foreground-400 hover:text-foreground-700" aria-label="清除搜索">
                <i className="ri-close-line" />
              </button>
            )}
          </div>
        </div>
      </section>

      <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "推荐榜单" }]} />

      <section className="container-page pb-12 md:pb-16">
        <div className="mb-6 flex flex-wrap gap-2">
          {filters.map((f) => {
            const isActive = active === f.slug;
            return (
              <button
                key={f.slug}
                type="button"
                onClick={() => setActive(f.slug)}
                className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-4 py-2 text-sm font-medium transition-colors ${
                  isActive
                    ? "bg-primary-500 text-background-50"
                    : "border border-background-200 bg-background-100 text-foreground-700 hover:border-primary-300 hover:text-primary-600"
                }`}
              >
                <i className={f.icon} />
                {f.name}
              </button>
            );
          })}
        </div>

        <p className="mb-5 text-sm text-foreground-500">
          共找到 <span className="font-bold text-primary-600">{list.length}</span> 部作品
          {q && <>，关键词「<span className="font-medium text-foreground-800">{q}</span>」</>}
        </p>

        {list.length > 0 ? (
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4 md:gap-5">
            {list.map((comic) => (
              <ComicCard key={comic.id} comic={comic} />
            ))}
          </div>
        ) : (
          <div className="flex flex-col items-center rounded-lg border border-dashed border-background-300 bg-background-100 px-6 py-16 text-center">
            <span className="flex h-14 w-14 items-center justify-center rounded-full bg-secondary-100 text-2xl text-secondary-600">
              <i className="ri-emotion-sad-line" />
            </span>
            <h2 className="mt-4 font-heading text-lg font-bold text-foreground-950">没有找到相关作品</h2>
            <p className="mt-2 text-sm text-foreground-600">换个关键词试试，或浏览全部推荐榜单。</p>
            <button
              type="button"
              onClick={() => {
                setActive("all");
                setKeyword("");
              }}
              className="mt-5 rounded-full bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
            >
              查看全部作品
            </button>
          </div>
        )}
      </section>

      <section className="bg-background-100">
        <div className="container-page py-10 md:py-12">
          <h2 className="font-heading text-lg font-bold text-foreground-950">快速前往栏目</h2>
          <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4 md:gap-5">
            {categories.map((c) => (
              <Link
                key={c.slug}
                to={`/category/${c.slug}`}
                className="group flex items-center gap-3 rounded-lg border border-background-200 bg-background-50 p-4 transition-colors hover:border-primary-300"
              >
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-secondary-100 text-lg text-secondary-700 group-hover:bg-primary-500 group-hover:text-background-50">
                  <i className={c.icon} />
                </span>
                <div className="min-w-0">
                  <h3 className="truncate text-sm font-bold text-foreground-950 group-hover:text-primary-700">{c.name}</h3>
                  <p className="truncate text-xs text-foreground-500">{c.subtitle}</p>
                </div>
                <i className="ri-arrow-right-s-line ml-auto text-lg text-foreground-400" />
              </Link>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}